import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Link as LinkIcon,
  Unlink,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Heading2,
  Heading3,
} from "lucide-react";
import { useEffect } from "react";

/**
 * RichTextEditor — minimal TipTap-based editor.
 *
 * Capabilities are opt-in via boolean props so we can reuse one component for:
 *   - Consent block (paragraph, bullets, bold, italic, links — default).
 *   - Form Introduction / Thank You (adds alignment, headings, ordered lists).
 *
 * Props:
 *  - value: HTML string
 *  - onChange: (html: string) => void
 *  - placeholder?: string
 *  - testId?: string
 *  - enableHeadings?: boolean (H2 + H3)
 *  - enableOrderedList?: boolean
 *  - enableAlignment?: boolean (left / center / right)
 *  - fontFamily?: string — applied to the typing surface for live font preview
 */
const FONT_STACKS = {
  default: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  serif: "'Playfair Display', 'Libre Baskerville', Georgia, serif",
  modern_sans: "'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif",
  elegant_sans: "'Outfit', 'Plus Jakarta Sans', system-ui, sans-serif",
};

export default function RichTextEditor({
  value,
  onChange,
  placeholder,
  testId = "rich-text",
  enableHeadings = false,
  enableOrderedList = false,
  enableAlignment = false,
  enableLinks = true,
  fontFamily,
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: enableHeadings ? { levels: [2, 3] } : false,
        blockquote: false,
        codeBlock: false,
        code: false,
        horizontalRule: false,
        orderedList: enableOrderedList ? {} : false,
        strike: false,
      }),
      ...(enableLinks
        ? [
            Link.configure({
              openOnClick: false,
              autolink: true,
              HTMLAttributes: {
                class: "text-indigo-600 underline",
                rel: "noopener noreferrer",
                target: "_blank",
              },
            }),
          ]
        : []),
      ...(enableAlignment
        ? [
            TextAlign.configure({
              types: ["heading", "paragraph", "listItem"],
              alignments: ["left", "center", "right", "justify"],
              defaultAlignment: "left",
            }),
          ]
        : []),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none focus:outline-none min-h-[120px] px-3 py-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1 [&_a]:text-indigo-600 [&_a]:underline [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1",
        "data-testid": `${testId}-editor`,
        style: fontFamily ? `font-family: ${FONT_STACKS[fontFamily] || FONT_STACKS.default}` : "",
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html === "<p></p>" ? "" : html);
    },
  });

  // Sync external value -> editor (e.g. when switching forms).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || "";
    if (current !== next && current !== "<p></p>") {
      editor.commands.setContent(next, false);
    } else if (!current && next) {
      editor.commands.setContent(next, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    let safe = url.trim();
    if (!/^https?:\/\//i.test(safe) && !/^mailto:/i.test(safe)) safe = `https://${safe}`;
    editor.chain().focus().extendMarkRange("link").setLink({ href: safe }).run();
  };

  // Apply text-align to EVERY block in the doc (paragraph, heading, listItem)
  // so alignment behaves as a content-wide property rather than per-cursor-block.
  // Cursor position is preserved.
  const setAlignmentAll = (alignment) => {
    const { from, to } = editor.state.selection;
    editor
      .chain()
      .focus()
      .selectAll()
      .setTextAlign(alignment)
      .setTextSelection({ from, to })
      .run();
  };

  const Btn = ({ onClick, isActive, label, children, tid }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      aria-label={label}
      title={label}
      data-testid={tid}
      className={`p-1.5 rounded-md transition-colors ${
        isActive
          ? "bg-indigo-100 text-indigo-700"
          : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
      }`}
    >
      {children}
    </button>
  );

  const Sep = () => <span className="w-px h-5 bg-zinc-200 mx-1" />;

  return (
    <div className="border rounded-lg bg-background overflow-hidden" data-testid={testId}>
      <div className="flex items-center gap-0.5 border-b px-2 py-1.5 bg-muted/30 overflow-x-auto">
        <Btn onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive("bold")} label="Bold" tid={`${testId}-bold`}>
          <Bold className="w-4 h-4" />
        </Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive("italic")} label="Italic" tid={`${testId}-italic`}>
          <Italic className="w-4 h-4" />
        </Btn>

        {enableHeadings && (
          <>
            <Sep />
            <Btn
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              isActive={editor.isActive("heading", { level: 2 })}
              label="Heading"
              tid={`${testId}-h2`}
            >
              <Heading2 className="w-4 h-4" />
            </Btn>
            <Btn
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              isActive={editor.isActive("heading", { level: 3 })}
              label="Subheading"
              tid={`${testId}-h3`}
            >
              <Heading3 className="w-4 h-4" />
            </Btn>
          </>
        )}

        {enableAlignment && (
          <>
            <Sep />
            <Btn
              onClick={() => setAlignmentAll("left")}
              isActive={editor.isActive({ textAlign: "left" })}
              label="Align left (entire content)"
              tid={`${testId}-align-left`}
            >
              <AlignLeft className="w-4 h-4" />
            </Btn>
            <Btn
              onClick={() => setAlignmentAll("center")}
              isActive={editor.isActive({ textAlign: "center" })}
              label="Align center (entire content)"
              tid={`${testId}-align-center`}
            >
              <AlignCenter className="w-4 h-4" />
            </Btn>
            <Btn
              onClick={() => setAlignmentAll("right")}
              isActive={editor.isActive({ textAlign: "right" })}
              label="Align right (entire content)"
              tid={`${testId}-align-right`}
            >
              <AlignRight className="w-4 h-4" />
            </Btn>
            <Btn
              onClick={() => setAlignmentAll("justify")}
              isActive={editor.isActive({ textAlign: "justify" })}
              label="Justify (entire content)"
              tid={`${testId}-align-justify`}
            >
              <AlignJustify className="w-4 h-4" />
            </Btn>
          </>
        )}

        <Sep />
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive("bulletList")} label="Bullet list" tid={`${testId}-bullet`}>
          <List className="w-4 h-4" />
        </Btn>
        {enableOrderedList && (
          <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive("orderedList")} label="Numbered list" tid={`${testId}-ordered`}>
            <ListOrdered className="w-4 h-4" />
          </Btn>
        )}

        {enableLinks && (
          <>
            <Sep />
            <Btn onClick={setLink} isActive={editor.isActive("link")} label="Add link" tid={`${testId}-link`}>
              <LinkIcon className="w-4 h-4" />
            </Btn>
            {editor.isActive("link") && (
              <Btn onClick={() => editor.chain().focus().unsetLink().run()} label="Remove link" tid={`${testId}-unlink`}>
                <Unlink className="w-4 h-4" />
              </Btn>
            )}
          </>
        )}
      </div>
      <div className="relative">
        {editor.isEmpty && placeholder && (
          <div className="absolute top-2 left-3 text-zinc-400 text-sm pointer-events-none">{placeholder}</div>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
